import React, { useRef, useState } from 'react';
import { UserRoundX, AlertTriangle } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { Button } from './ui/Button';
import { TextInput } from './ui/TextInput';
import { useToast } from './ui/Toast';
import { solicitudRolService } from '../services/solicitudRolService';
import { Usuario } from '../types/auth';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';

interface EliminarRolModalProps {
  readonly isOpen: boolean;
  readonly usuario: Usuario;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export const EliminarRolModal: React.FC<EliminarRolModalProps> = ({
  isOpen,
  usuario,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [motivo, setMotivo] = useState('');

  const resetForm = () => {
    setMotivo('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await solicitudRolService.crearEliminacion(usuario.id, motivo);
      resetForm();
      toast.success('Solicitud de eliminación enviada. Será revisada por un administrador.');
      onSuccess();
      onClose();
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Error al crear la solicitud';
      reportarErrorInesperado(e, errorMessage);

    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={handleClose}
      title="Eliminar Rol"
      icon={<div className="bg-red-100 p-2 rounded-full"><UserRoundX className="w-5 h-5 text-red-600" /></div>}
      size="sm"
      hasUnsavedChanges={motivo.trim() !== ''}
      closeRef={closeRef}
    >
      <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">

        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Miembro</p>
            <p className="text-sm font-medium text-slate-900">
              {usuario.nombres} {usuario.apellidos}
            </p>
            <p className="text-xs text-slate-600">{usuario.email}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase">Rol Actual</p>
            <p className="text-sm font-medium text-slate-900">{usuario.rol.nombre}</p>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            Esta acción debe ser aprobada por un administrador de jerarquía superior antes de aplicarse.
            El miembro conservará su registro e historial; solo perderá su rol en la aplicación.
          </p>
        </div>

        <TextInput
          label="Motivo de la Eliminación"
          value={motivo}
          onChange={(v) => { setMotivo(v); }}
          placeholder="Termina periodo de 2 años como Presidente JAL"
          required
        />

        <div className="flex gap-3">
          <Button type="submit" variant="danger" disabled={loading || !motivo.trim()}>
            <UserRoundX className="w-4 h-4 mr-2" />
            {loading ? 'Enviando...' : 'Enviar Solicitud'}
          </Button>
          <Button type="button" variant="outline" onClick={requestClose}>
            Cancelar
          </Button>
        </div>
      </form>
    </DialogShell>
  );
};
